import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import {
  RepoNameStatus,
  validateRepoNameStatus,
  CopyCodefilesField,
  validateCopyCodefiles,
} from './RepoNameStatus';
import { UserTeamsPicker, validateUserTeams } from './UserTeamsPicker';
import { OrgTeamsPicker, validateOrgTeams } from './OrgTeamsPicker';
import { GitignorePicker } from './GitignorePicker';
import { LifecyclePicker } from './LifecyclePicker';
import { TagField } from './TagField';

/**
 * Registers the `GitHubRepo_RepoNameStatus` custom field used by the
 * Standardized GitHub Repository Creation template. It shows a live, debounced
 * availability status under the repository-name input and, on "Next", runs a
 * validation that (1) checks the entered repository name against the
 * naming/format rules (allowed characters, length 1–100, no leading/trailing
 * `.` or `-`, no consecutive special characters) and (2) blocks progression
 * when the name already exists in the selected organization. While mounted it
 * also adjusts the sibling RepoUrlPicker: it hides the fixed single-host
 * control and replaces the built-in repository-name helper text with
 * Principal's naming-pattern guidance and a docs link (opens in a new tab).
 */
export const GitHubRepoNameStatusFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'GitHubRepo_RepoNameStatus',
    component: RepoNameStatus,
    validation: validateRepoNameStatus,
  }),
);

/**
 * Registers the `GitHubRepo_UserTeamsPicker` custom field used by the
 * Standardized GitHub Repository Creation template. It lists only the
 * AD-backed GitHub teams (`kind: Group`, `spec.type: team`) that the current
 * user belongs to, and stores the selected team as a full entity ref
 * (`group:default/<team-slug>`) so downstream templating keeps working.
 */
export const UserTeamsPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'GitHubRepo_UserTeamsPicker',
    component: UserTeamsPicker,
    validation: validateUserTeams,
  }),
);

/**
 * Registers the `GitHubRepo_OrgTeamsPicker` custom field used by the
 * Standardized GitHub Repository Creation template. It lists — live from the
 * GitHub API — only the GitHub teams the current user belongs to within the
 * organization selected on the first step, and stores the bare team slug so
 * downstream GitHub-native consumers (admin-access grant, CODEOWNERS) work.
 */
export const OrgTeamsPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'GitHubRepo_OrgTeamsPicker',
    component: OrgTeamsPicker,
    validation: validateOrgTeams,
  }),
);

/**
 * Registers the `GitHubRepo_GitignorePicker` custom field used by the
 * Standardized GitHub Repository Creation template (labeled "Languages"). It
 * renders the (long) .gitignore template list as a scrollable, checkbox
 * multi-select.
 */
export const GitignorePickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'GitHubRepo_GitignorePicker',
    component: GitignorePicker,
  }),
);

/**
 * Registers the `GitHubRepo_TagField` custom field used by the Standardized
 * GitHub Repository Creation template. It is applied per array ITEM
 * (`items.ui:field`), so the default array widget (add / remove / reorder
 * chrome) is preserved and only each tag input is customised to show a short,
 * specific error inline as the user types. Validity/"Next" blocking is handled
 * by the schema (`pattern`, `minLength`/`maxLength`, `maxItems`,
 * `uniqueItems`), so no field validator is registered here.
 */
export const TagFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'GitHubRepo_TagField',
    component: TagField,
  }),
);

/**
 * Registers the `GitHubRepo_LifecyclePicker` custom field used by the
 * Standardized GitHub Repository Creation template. It enforces the Sandbox
 * governance rule reactively: for the Sandbox org (`principalsbx-emu`) it forces
 * the value to `experimental` and locks the control, and when the org is
 * switched away from Sandbox it clears the forced value so the user chooses
 * deliberately. A plain JSON-Schema `const` conditional could not do the latter
 * because RJSF never retracts a value it injected once the condition stops
 * matching.
 */
export const LifecyclePickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'GitHubRepo_LifecyclePicker',
    component: LifecyclePicker,
  }),
);

/**
 * Registers the `GitHubRepo_CopyCodefiles` custom field used by the
 * Standardized GitHub Repository Creation template. It renders an OPTIONAL
 * "Add code files from an existing repository" URL input and validates the
 * pasted URL live as the user types — a debounced inline status that flags a
 * non-Principal owner or a missing source and clears on every edit. The same
 * rules are enforced server-side by Repo Foundry, which rolls back if the
 * source can't be read. Blank leaves the value undefined and the normal flow
 * unchanged; a value is the source repo URL string that Repo Foundry copies
 * into the new repo as a second commit after governance.
 */
export const CopyCodefilesFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'GitHubRepo_CopyCodefiles',
    component: CopyCodefilesField,
    validation: validateCopyCodefiles,
  }),
);
